import { useEffect } from 'react';
import messenger, { MessengerEvent } from 'utils/legacy/messenger';
import { type AnyMessengerMessage } from 'utils/legacy/messengerTypes';
import { type AnyWebsocketMessage } from 'utils/websockets';

/**
 * A custom hook for adding subscriptions to the el8-to-React messenger. Use this for
 * client-to-client messenger events - for websocket events, use `useWebsocketListener` instead.
 */
export default function useMessengerListener<TMessengerEvent extends MessengerEvent>(
  event: TMessengerEvent,
  handlerFn: (payload?: Extract<AnyMessengerMessage, { type: TMessengerEvent }>) => void,
): void {
  useEffect(() => {
    messenger.on<Extract<AnyMessengerMessage, { type: TMessengerEvent }>>(event, handlerFn);
    return (): void => {
      messenger.off<Extract<AnyMessengerMessage, { type: TMessengerEvent }>>(event, handlerFn);
    };
  }, [event, handlerFn]);
}

/**
 * A custom hook for listening to websocket messages for a specific document type.
 */
export function useWebsocketListener(
  handlerFn: (message?: AnyWebsocketMessage) => void,
  channel?: string,
): void {
  useEffect(() => {
    messenger.on<AnyWebsocketMessage>(MessengerEvent.WebsocketEvent, handlerFn, channel);
    return (): void => {
      messenger.off<AnyWebsocketMessage>(MessengerEvent.WebsocketEvent, handlerFn, channel);
    };
  }, [channel, handlerFn]);
}
